'use client';

import { cn } from '@/lib/utils';
import type { ArenaModality, ArenaExperimentStatus, ArenaGenerationStatus } from '@/lib/arena/types';
import {
  MODALITY_META,
  STATUS_ACCENT,
  STATUS_LABEL,
  GEN_STATUS_ACCENT,
} from './constants';

export function ModalityBadge({ modality, className }: { modality: ArenaModality; className?: string }) {
  const meta = MODALITY_META[modality];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        meta.accent,
        className,
      )}
    >
      {meta.label}
    </span>
  );
}

export function StatusChip({ status, className }: { status: ArenaExperimentStatus; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
        STATUS_ACCENT[status] ?? STATUS_ACCENT.draft,
        className,
      )}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

export function GenStatusChip({ status, className }: { status: ArenaGenerationStatus; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
        GEN_STATUS_ACCENT[status] ?? GEN_STATUS_ACCENT.queued,
        className,
      )}
    >
      {status}
    </span>
  );
}

export function SpendMeter({
  spent,
  cap,
  className,
}: {
  spent: number;
  cap: number;
  className?: string;
}) {
  const pct = cap > 0 ? Math.min(100, (spent / cap) * 100) : 0;
  const danger = pct >= 90;
  const warn = pct >= 60 && pct < 90;
  return (
    <div className={cn('w-full', className)}>
      <div className="mb-1 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>
          {spent} / {cap} cr
        </span>
        <span>{pct.toFixed(0)}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            danger ? 'bg-red-400' : warn ? 'bg-amber-400' : 'bg-emerald-400',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
